import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import { ArrowLeft, Phone, Calendar, Clock, Users, User, Edit, Shield } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Separator } from '@/components/ui/separator'
import { LoadingPage } from '@/components/ui/spinner'
import { getInitials } from '@/lib/utils'
import type { TipoPermissao, UsuarioAdmin } from '@/hooks/use-admin'
import { useUsuarios } from '@/hooks/use-admin'

const PAPEL_LABELS: Record<NonNullable<TipoPermissao>, string> = {
  admin: 'Administrador',
  gestor: 'Gestor',
  lider: 'Líder',
  membro: 'Membro',
}

const PAPEL_COLORS: Record<NonNullable<TipoPermissao>, string> = {
  admin: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300',
  gestor: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300',
  lider: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300',
  membro: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300',
}

export function MembrosDetalhePage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [membro, setMembro] = useState<UsuarioAdmin | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const { data: usuarios } = useUsuarios()

  useEffect(() => {
    const fetchMembro = async () => {
      setIsLoading(true)
      try {
        // Simular busca do membro
        await new Promise((resolve) => setTimeout(resolve, 500))

        const membroEncontrado = usuarios?.find((m: UsuarioAdmin) => m.id === id)
        setMembro(membroEncontrado || null)
      } catch (error) {
        console.error('Erro ao buscar membro:', error)
      } finally {
        setIsLoading(false)
      }
    }

    if (id) {
      fetchMembro()
    }
  }, [id, usuarios])

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    })
  }

  const formatDateTime = (dateString: string) => {
    return new Date(dateString).toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  if (isLoading) {
    return <LoadingPage message="Carregando informações do membro..." variant="dots" />
  }

  if (!membro) {
    return (
      <div className="space-y-6 px-4 py-6 sm:px-6 lg:px-6 lg:py-8 xl:px-8">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate(-1)}
            className="h-8 w-8 p-0"
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Membro não encontrado</h1>
            <p className="text-muted-foreground">O membro solicitado não foi encontrado</p>
          </div>
        </div>
        <Card>
          <CardContent className="p-12 text-center">
            <User className="mx-auto mb-4 h-12 w-12 text-muted-foreground" />
            <h3 className="mb-2 text-lg font-semibold">Membro não encontrado</h3>
            <p className="mb-4 text-sm text-muted-foreground">
              O membro com ID "{id}" não foi encontrado no sistema.
            </p>
            <Button onClick={() => navigate(-1)}>Voltar para Membros</Button>
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="space-y-6 px-4 py-6 sm:px-6 lg:px-6 lg:py-8 xl:px-8">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex items-center justify-between"
      >
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate(-1)}
            className="h-8 w-8 p-0"
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <h1 className="text-3xl font-bold tracking-tight">{membro.nome}</h1>
            <p className="text-muted-foreground">Detalhes do membro</p>
          </div>
        </div>
        <Button className="shadow-md shadow-primary/20">
          <Edit className="mr-2 h-4 w-4" />
          Editar
        </Button>
      </motion.div>

      <div className="grid gap-6 lg:grid-cols-3">
        {/* Informações Principais */}
        <div className="space-y-6 lg:col-span-2">
          {/* Perfil */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
          >
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <User className="h-5 w-5" />
                  Informações Pessoais
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="flex items-start gap-4">
                  <Avatar className="h-20 w-20 border-4 border-background shadow-lg">
                    <AvatarImage src={undefined} />
                    <AvatarFallback className="bg-gradient-to-br from-primary to-primary/70 text-xl text-white">
                      {getInitials(membro.nome)}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1 space-y-2">
                    <h3 className="text-xl font-semibold">{membro.nome}</h3>
                    <Badge className={PAPEL_COLORS[membro.permissao || 'membro']}>
                      {PAPEL_LABELS[membro.permissao || 'membro']}
                    </Badge>
                    <div className="flex items-center gap-4 text-sm text-muted-foreground">
                      <div className="flex items-center gap-1">
                        <Calendar className="h-4 w-4" />
                        Membro desde {formatDate(membro.criado_em || new Date().toISOString())}
                      </div>
                    </div>
                  </div>
                </div>

                <Separator />

                <div className="grid gap-4 sm:grid-cols-2">
                  {membro.telefone && (
                    <div className="flex items-center gap-3">
                      <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-muted">
                        <Phone className="h-4 w-4" />
                      </div>
                      <div>
                        <p className="text-sm font-medium">Telefone</p>
                        <p className="text-sm text-muted-foreground">{membro.telefone}</p>
                      </div>
                    </div>
                  )}

                  <div className="flex items-center gap-3">
                    <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-muted">
                      <Shield className="h-4 w-4" />
                    </div>
                    <div>
                      <p className="text-sm font-medium">Papel</p>
                      <p className="text-sm text-muted-foreground">{PAPEL_LABELS[membro.permissao || 'membro']}</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>

          {/* Atividades Recentes */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Clock className="h-5 w-5" />
                  Atividades Recentes
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex items-center gap-3 rounded-lg bg-muted/50 p-3">
                    <div className="h-2 w-2 rounded-full bg-green-500" />
                    <div className="flex-1">
                      <p className="text-sm font-medium">Inscrito no evento "Culto de Jovens"</p>
                      <p className="text-xs text-muted-foreground">Há 2 dias</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 rounded-lg bg-muted/50 p-3">
                    <div className="h-2 w-2 rounded-full bg-blue-500" />
                    <div className="flex-1">
                      <p className="text-sm font-medium">Perfil atualizado</p>
                      <p className="text-xs text-muted-foreground">Há 1 semana</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 rounded-lg bg-muted/50 p-3">
                    <div className="h-2 w-2 rounded-full bg-purple-500" />
                    <div className="flex-1">
                      <p className="text-sm font-medium">Adicionado ao ministério de Louvor</p>
                      <p className="text-xs text-muted-foreground">Há 2 semanas</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Estatísticas */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
          >
            <Card>
              <CardHeader>
                <CardTitle>Estatísticas</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Eventos participados</span>
                  <span className="font-semibold">12</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Ministérios</span>
                  <span className="font-semibold">2</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Frequência</span>
                  <span className="font-semibold">85%</span>
                </div>
              </CardContent>
            </Card>
          </motion.div>

          {/* Ministérios */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
          >
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Users className="h-5 w-5" />
                  Ministérios
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex items-center gap-3 rounded-lg bg-muted/50 p-3">
                  <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary/10">
                    <Users className="h-4 w-4 text-primary" />
                  </div>
                  <div>
                    <p className="text-sm font-medium">Louvor</p>
                    <p className="text-xs text-muted-foreground">Membro</p>
                  </div>
                </div>
                <div className="flex items-center gap-3 rounded-lg bg-muted/50 p-3">
                  <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary/10">
                    <Users className="h-4 w-4 text-primary" />
                  </div>
                  <div>
                    <p className="text-sm font-medium">Jovens</p>
                    <p className="text-xs text-muted-foreground">Líder</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>

          {/* Informações do Sistema */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
          >
            <Card>
              <CardHeader>
                <CardTitle>Informações do Sistema</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3 text-sm">
                <div>
                  <span className="text-muted-foreground">ID:</span>
                  <p className="font-mono text-xs">{membro.id}</p>
                </div>
                <div>
                  <span className="text-muted-foreground">Criado em:</span>
                  <p>{formatDateTime(membro.criado_em || new Date().toISOString())}</p>
                </div>
                <div>
                  <span className="text-muted-foreground">Atualizado em:</span>
                  <p>{formatDateTime(membro.atualizado_em || new Date().toISOString())}</p>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </div>
      </div>
    </div>
  )
}
